/* eslint-disable react/prop-types */
import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import IcomoonReact from 'icomoon-react';
import { useTheme } from 'emotion-theming';
import SelectDropdownExercisePicker from './SelectDropdownExercisePicker';
import Text from '../../../../shared/components/Text/Text';
import MonthEditPopoverContext from './MonthEditPopoverContext';
import useUserScrollIntent from '../../hooks/useUserScrollIntent';
import iconSet from '../../../../shared/images/teambuildr-selection.json';

const RowContainer = styled('div')`
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 4px;
  padding: 4px 0;
`;

const ButtonAndLabel = styled('div')`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const ButtonIcon = styled('div')`
  padding: 4px;
  background: ${({ theme }) => theme.colors.buttonPrimaryBg};
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 25px;
  min-width: 25px;
  max-width: 25px;
  max-height: 25px;
  margin-left: 2px;
  flex-shrink: 0;
`;

const PickerWrapper = styled('div')`
  flex: 1;
  min-width: 0;
  margin-left: 5px;
`;

/**
 * "+ Add Exercise" row rendered at the bottom of a month-view day cell.
 * Mirrors the day/week ExercisePicker pattern:
 *  - SelectDropdownExercisePicker mounts only while open. Month view
 *    renders 70 day cells, and an always-mounted picker per cell held
 *    70 copies of the account's exercise list (crashed accounts with
 *    ~15k exercises).
 *  - The "+" + "Add Exercise" label live in a sibling button; the
 *    label disappears when the picker is open.
 *  - onMouseDown (not onClick) flips the state, matching the existing
 *    add-button behavior in day/week view.
 *
 * After the user picks an exercise, the picker internally dispatches
 * `addPreSavedItemToCalendarItemsArray`. We diff assignedIds before
 * vs. after the picker opens, find the new preSave item, and
 * auto-open the month-view edit popover anchored to its row so the
 * PreSaveTable comes up next to it.
 */
const MonthAddExerciseRow = ({ date }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const containerRef = useRef(null);
  const prevAssignedIdsRef = useRef(new Set());
  /** Latched when the picker opens; stays true across react-select's
   * own close-on-select so the detection useEffect can still match the
   * new item after pickerOpen has flipped back to false. Cleared once
   * we detect-and-handle, or once the picker closes without adding. */
  const expectingNewItemRef = useRef(false);
  const { openPopover } = useContext(MonthEditPopoverContext);
  const isUserScroll = useUserScrollIntent();

  /** Month view's underlying rendering doesn't yet support program-
   * library mode (items live in nonDateBasedProgramItems[dayNumber],
   * not calendarItems[date]). Hide the add-exercise row entirely in
   * that mode so we don't dispatch preSave items to a date that has
   * no meaning in the program-library data model. */
  const programType = useSelector((state) => state.calendar.data.programType);

  const formattedDate = moment(date).format('YYYY-MM-DD');
  const exercises = useSelector(
    (state) => state.calendar.data.calendarItems[formattedDate]?.exercises,
  );
  const { colors } = useTheme();

  /** Close the picker if the calendar (or any ancestor) scrolls so
   * the input doesn't float over the grid. Skip scrolls inside the
   * picker's own option menu — those are the user scrolling the
   * dropdown results and should keep the picker open. Also skip scrolls
   * inside this row: once the search text outgrows a narrow cell,
   * react-select's value container scrolls sideways to keep the caret
   * in view, and that isn't the calendar scrolling. Browser-made scrolls,
   * like lifting the focused input above a tablet's keyboard, are skipped
   * too. */
  useEffect(() => {
    if (!pickerOpen) return undefined;
    const close = (e) => {
      if (!isUserScroll()) return;
      if (e.target && e.target.closest && e.target.closest('[class*="__menu"]')) return;
      if (e.target instanceof Node && containerRef.current?.contains(e.target)) return;
      setPickerOpen(false);
    };
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [pickerOpen, isUserScroll]);

  /** Snapshot assignedIds at the moment the picker opens, and arm the
   * "expecting a new item" latch. */
  useEffect(() => {
    if (pickerOpen) {
      prevAssignedIdsRef.current = new Set(
        (exercises || []).map((ex) => ex.assignedId),
      );
      expectingNewItemRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerOpen]);

  /** Detect newly-added preSave item and open the edit popover. */
  useEffect(() => {
    if (!expectingNewItemRef.current) return;
    if (!exercises) return;
    const newItem = exercises.find(
      (ex) => ex.preSave && !prevAssignedIdsRef.current.has(ex.assignedId),
    );
    if (!newItem) {
      /** Picker closed without an addition (user dismissed). Clear
       * the latch so future external changes to `exercises` don't
       * trigger a spurious popover. */
      if (!pickerOpen) expectingNewItemRef.current = false;
      return;
    }
    expectingNewItemRef.current = false;
    setSelectedOption(null);
    requestAnimationFrame(() => {
      const row = document.querySelector(
        `[data-assigned-id="${newItem.assignedId}"]`,
      );
      openPopover({
        content: newItem,
        date,
        anchorEl: row || containerRef.current,
      });
    });
  }, [exercises, pickerOpen, openPopover, date]);

  const handleClick = () => {
    /** Defer past the current event tick so
     * SelectDropdownExercisePicker's `handleClickOutside` — a document
     * mousedown listener — doesn't see the freshly-mounted Select and
     * the "+" click target as an outside-click, immediately resetting
     * `addExerciseToggleMenuOpen` to false (same race as the swap
     * dropdown). By the time setTimeout fires, the current mousedown
     * event is done and the listener has already run while
     * selectRef.current was still null. */
    setTimeout(() => setPickerOpen((open) => !open), 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  if (programType === 'free-form-program') return null;

  return (
    <RowContainer ref={containerRef}>
      <ButtonAndLabel
        onMouseDown={handleClick}
        onKeyDown={handleKeyDown}
        role='button'
        tabIndex={0}
        aria-label='Add exercise'
      >
        <ButtonIcon>
          <IcomoonReact
            iconSet={iconSet}
            size={12}
            icon='plus'
            color={colors.white}
          />
        </ButtonIcon>
        {!pickerOpen && (
          <Text style={{
            color: colors.text,
            whiteSpace: 'nowrap',
            marginLeft: 7,
            fontSize: 13,
          }}
          >
            Add Exercise
          </Text>
        )}
      </ButtonAndLabel>
      <PickerWrapper>
        {pickerOpen && (
          <SelectDropdownExercisePicker
            fullWidthInput
            placeholder='Search Exercises'
            addExercisePicker
            addExerciseToggleMenuOpen={pickerOpen}
            setAddExerciseToggleMenuOpen={setPickerOpen}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            tabIndex={-1}
            date={date}
            isWeekView={false}
            disableMenuScrollBlock
          />
        )}
      </PickerWrapper>
    </RowContainer>
  );
};

export default MonthAddExerciseRow;
